import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { MarkItViewApi } from '../../../Apis/Apis';
import { MarkItViewSavedQueryModel } from '../../../Generated/Mark-It-View-Api';
import { RootState, useApplicationSelector } from '../../../Redux/ReduxStore';
import { StoneXButton } from '../../StoneXMui';
import { isValidEmail } from '../EmailSchedules/EmailScheduleDraft';
import RecipientsInput from '../EmailSchedules/RecipientsInput';

interface SendSavedQueryEmailDialogProps {
  savedQuery: MarkItViewSavedQueryModel | null;
  onClose: () => void;
}

export default function SendSavedQueryEmailDialog(props: SendSavedQueryEmailDialogProps) {
  const { savedQuery, onClose } = props;
  const userEmail = useApplicationSelector((state: RootState) => state.userAccess.email);

  const [recipients, setRecipients] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string }>();

  useEffect(() => {
    if (savedQuery) {
      setRecipients(userEmail ? [userEmail] : []);
      setSubject('');
      setResult(undefined);
    }
  }, [savedQuery, userEmail]);

  const recipientsError =
    recipients.length === 0
      ? 'Add at least one recipient'
      : recipients.some((recipient) => !isValidEmail(recipient))
        ? 'One or more addresses are not valid'
        : undefined;

  const send = () => {
    if (!savedQuery || recipientsError) {
      return;
    }

    setIsSending(true);
    setResult(undefined);

    MarkItViewApi.sendMarketViewSavedQueryEmail(undefined, String(savedQuery.savedQueryId), {
      savedQueryId: savedQuery.savedQueryId,
      recipients,
      subject: subject.trim() === '' ? null : subject.trim(),
    })
      .then((response) => {
        const sentTo = response.data.recipients?.join(', ') ?? recipients.join(', ');
        setResult({ ok: true, message: `Sent "${response.data.subject}" to ${sentTo}.` });
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail ?? error?.message ?? 'unknown error';
        setResult({ ok: false, message: `The email could not be sent: ${detail}` });
      })
      .finally(() => setIsSending(false));
  };

  return (
    <Dialog open={!!savedQuery} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Email "{savedQuery?.name}"</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Runs the saved query against the latest quote date and emails the results as an Excel workbook. Nothing is scheduled.
        </DialogContentText>
        <RecipientsInput value={recipients} onChange={setRecipients} error={recipientsError} disabled={isSending} autoFocus />
        <TextField
          label="Subject (optional)"
          variant="standard"
          fullWidth
          margin="normal"
          disabled={isSending}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          helperText="Defaults to the query name and quote date"
        />
        {result && (
          <DialogContentText sx={{ mt: 1 }} color={result.ok ? 'success.main' : 'error'}>
            {result.message}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result?.ok ? 'Close' : 'Cancel'}</Button>
        <StoneXButton onClick={send} disabled={!!recipientsError} isWorking={isSending}>
          Send
        </StoneXButton>
      </DialogActions>
    </Dialog>
  );
}
